import { createStore } from 'zustand/vanilla'

// The subway stations currently drawn on the map. TransitMap is the single fetch owner — it
// already loads /api/stops/with-routes for the active schedule — and mirrors whatever it
// draws here, so other components read the same set without re-fetching or re-implementing
// the schedule logic.
export interface StopEntry {
  id: string
  name: string
  lat: number
  lon: number
  routes: string[]   // subway lines serving the station, or bus route short names serving the stop
  // Subway only: `routes` widened by the in-system transfer graph wherever a walkable
  // transfer crosses a complex boundary. Absent for bus stops and older backends — fall
  // back to `routes`.
  transferRoutes?: string[]
  // Subway only: lines behind an overnight-closing passage, on top of transferRoutes.
  limitedTransfers?: Array<{ routes: string[]; opensHour: number; closesHour: number }>
  // Subway only: the GTFS parent stations MTA merges into this complex, including `id`, so
  // a lookup by a pre-merge id still finds its station.
  memberIds?: string[]
  accessible?: boolean
}

interface StopsState {
  stops: StopEntry[]       // subway parent stations for the active schedule (~500)
  busStops: StopEntry[]    // every NYC bus stop (~15k); lazy-loaded when the bus/all filter is shown
  // Bumped by any consumer needing the bus-stop set on a subway-filtered session — the
  // nearby panel lists bus stops regardless of filter. TransitMap watches this, kicks off
  // the lazy load and mirrors the result into busStops. A counter, not a bool, so repeated
  // asks re-fire.
  busStopsRequest: number
  setStops: (stops: StopEntry[]) => void
  setBusStops: (busStops: StopEntry[]) => void
  requestBusStops: () => void
}

export const useStopsStore = createStore<StopsState>((set) => ({
  stops: [],
  busStops: [],
  busStopsRequest: 0,
  setStops: (stops) => set({ stops }),
  setBusStops: (busStops) => set({ busStops }),
  requestBusStops: () => set((s) => ({ busStopsRequest: s.busStopsRequest + 1 })),
}))
